/**
 * DocumentTypeEditorModal — edytor typu dokumentu z zakładkami (ADR-0085, E2.5).
 *
 * „Edytuj" na wierszu TYPU w Kartotekach otwiera JEDEN modal z dwiema zakładkami:
 *  - „Ustawienia typu" — formularz jak w panelu „Globalne typy dokumentów"
 *    (kod/nazwa/opis/numeracja/kolejność + centralny layout formularza + domyślny proces).
 *    Prefill z `GET /admin/global-document-types/:id`, zapis diffem przez `PUT` na ten sam id.
 *  - „Pola" — bramkowanie pól z puli klasy per typ (przeniesione z `TypeFieldGatingModal`).
 *
 * Zakładki mają osobne cele zapisu (typ ↔ katalog ADM; bramkowanie ↔ tabela typ↔pole),
 * więc każda niesie własną stopkę z przyciskiem „Zapisz". Przełączenie zakładki
 * odmontowuje poprzednią (niezapisane zmiany aktywnej zakładki przepadają — zapisz
 * przed przełączeniem).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminGlobalDocumentType } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import {
  type DefaultProcessTemplateOption,
  type FormViewOption,
  GlobalDocumentTypeFormModal,
  type GlobalDocumentTypeFormValues,
} from '@/components/documents/GlobalDocumentTypeFormModal';
import { buildGlobalNumberingBaseFieldOptions } from '@/components/documents/useNumberingBaseFieldOptions';
import { TypeFieldGatingPanel } from '@/components/kartoteki/TypeFieldGatingPanel';
import { Modal } from '@/components/Modal';
import { Tabs } from '@/components/Tabs';
import { useToast } from '@/contexts/ToastContext';
import { listAdminProcessTemplates } from '@/lib/admin-process-templates/api';
import { api } from '@/lib/api';
import { getKartotekaSlugForClass } from '@/lib/documents/class-kartoteka-slug';
import {
  buildUpdatePayload,
  handleHrAbsenceSourceErrorCode,
  toFormValues,
} from '@/lib/documents/global-document-type-form';
import {
  useKartotekaDefinition,
  useKartotekaSystemViews,
} from '@/lib/kartoteki/kartoteka-definition';

/** ADR-0070 — cel procesu obszaru Dokumenty (stały, wspólny dla wszystkich 7 klas). */
const DOCUMENTS_PROCESS_TARGET_MODULE_CODE = 'crm_core.documents';

/** Cel edytora typu — wiersz TYPU w Kartotekach. */
export interface DocumentTypeEditorTarget {
  /** Slug klasy-rodzica (kartoteka bazowa pól, np. 'internal'). */
  slug: string;
  /** global_document_types.id — prefill/zapis ustawień, adres bramkowania pól. */
  typeId: string;
  /** Nazwa typu — tytuł modala (do czasu prefillu z serwera). */
  typeName: string;
  /** Nazwa klasy/kartoteki (np. „Dokumenty wewnętrzne") — kontekst w tytule. */
  className: string;
}

type EditorTab = 'settings' | 'fields';

function extractErrorCode(err: unknown): string | null {
  if (!isAxiosError(err)) return null;
  return (err.response?.data?.error as string | undefined) ?? null;
}

export function DocumentTypeEditorModal({
  target,
  onClose,
}: {
  target: DocumentTypeEditorTarget | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<EditorTab>('settings');
  const [formServerErrors, setFormServerErrors] = useState<Record<string, string>>({});
  // Niezapisane zmiany w osadzonym formularzu „Ustawienia typu" (zgłasza je formularz). Zakładka
  // „Pola" zapisuje się osobno i nie jest objęta pytaniem.
  const [settingsDirty, setSettingsDirty] = useState(false);

  const typeId = target?.typeId;
  const settingsActive = target !== null && activeTab === 'settings';

  // Pełny rekord typu — prefill zakładki „Ustawienia". `GET :id` zwraca klasę,
  // status i wszystkie pola (czysty prefill, bez mapowania slug↔klasa).
  const typeQ = useQuery<AdminGlobalDocumentType>({
    queryKey: ['admin', 'global-document-types', 'detail', typeId ?? null],
    queryFn: async () => {
      const res = await api.get<{ type: AdminGlobalDocumentType }>(
        `/admin/global-document-types/${typeId}`,
      );
      return res.data.type;
    },
    enabled: Boolean(typeId),
    retry: false,
  });
  const type = typeQ.data;

  // Widoki FORM biblioteki kartoteki tej klasy → picker „Widok formularza (centralnie)".
  // Enabled tylko gdy zakładka Ustawienia aktywna i znamy klasę (slug kartoteki).
  const kartotekaSlug = type ? getKartotekaSlugForClass(type.class) : undefined;
  const viewsQ = useKartotekaSystemViews(
    settingsActive && kartotekaSlug ? kartotekaSlug : undefined,
  );
  const formViewOptions: FormViewOption[] = (viewsQ.data ?? [])
    .filter((v) => v.viewType === 'form')
    .map((v) => ({ slug: v.slug, name: v.name }));
  const formViewsUnavailable = viewsQ.isError;

  // Katalog CENTRALNY (ADM) pól dodatkowych tej klasy → picker „Pole daty numeracji"
  // (mirror `AdminGlobalDocumentTypes`). Ten edytor edytuje ZAWSZE jeden, już istniejący
  // typ (`typeId` z `target`, nigdy tworzenie) — więc jedna definicja, widok TYPU
  // (pola wspólne klasy ∪ prywatne TEGO typu, zgodne z walidacją PUT).
  const definitionQ = useKartotekaDefinition(
    settingsActive && kartotekaSlug ? kartotekaSlug : undefined,
    typeId,
  );
  const baseFieldOptions = useMemo(
    () =>
      type ? buildGlobalNumberingBaseFieldOptions(type.class, definitionQ.data?.fields, t) : [],
    [type, definitionQ.data, t],
  );

  // Opublikowane szablony procesu celu `crm_core.documents` → picker „Domyślny proces".
  const templatesQ = useQuery({
    queryKey: ['admin', 'process-templates', DOCUMENTS_PROCESS_TARGET_MODULE_CODE] as const,
    queryFn: () =>
      listAdminProcessTemplates({
        isPublished: true,
        targetModuleCode: DOCUMENTS_PROCESS_TARGET_MODULE_CODE,
      }),
    enabled: settingsActive,
    retry: false,
  });
  const templateOptions: DefaultProcessTemplateOption[] = (templatesQ.data?.data ?? []).map(
    (tpl) => ({ id: tpl.id, name: tpl.name }),
  );
  const templatesUnavailable = templatesQ.isError;

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      values,
      original,
    }: {
      id: string;
      values: GlobalDocumentTypeFormValues;
      original: AdminGlobalDocumentType;
    }) => {
      const res = await api.put<{ type: AdminGlobalDocumentType }>(
        `/admin/global-document-types/${id}`,
        buildUpdatePayload(values, original),
      );
      return res.data.type;
    },
    onSuccess: () => {
      // Nazwa/kod typu widnieje w drzewku Kartotek — odśwież je oraz stronę typów globalnych.
      queryClient.invalidateQueries({ queryKey: ['admin', 'kartoteki'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'global-document-types'] });
      toast.show({ variant: 'success', title: t('adminDocumentTypes.toast.updated') });
      setFormServerErrors({});
      onClose();
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      if (code === 'CODE_TAKEN') {
        setFormServerErrors({ code: t('adminDocumentTypes.toast.codeTaken') });
        return;
      }
      if (code === 'NAME_TAKEN') {
        setFormServerErrors({ name: t('adminDocumentTypes.toast.nameTaken') });
        return;
      }
      if (code === 'CODE_IMMUTABLE') {
        toast.show({ variant: 'warning', title: t('adminDocumentTypes.toast.codeImmutable') });
        return;
      }
      if (code === 'FORM_VIEW_NOT_FOUND') {
        toast.show({ variant: 'warning', title: t('adminDocumentTypes.toast.formViewNotFound') });
        return;
      }
      if (code === 'FORM_VIEW_NOT_FORM') {
        toast.show({ variant: 'warning', title: t('adminDocumentTypes.toast.formViewNotForm') });
        return;
      }
      if (code === 'FORM_VIEW_CLASS_MISMATCH') {
        toast.show({
          variant: 'warning',
          title: t('adminDocumentTypes.toast.formViewClassMismatch'),
        });
        return;
      }
      if (code === 'DEFAULT_PROCESS_TEMPLATE_NOT_FOUND') {
        toast.show({
          variant: 'warning',
          title: t('adminDocumentTypes.toast.defaultProcessTemplateNotFound'),
        });
        return;
      }
      if (code === 'DEFAULT_PROCESS_TEMPLATE_NOT_PUBLISHED') {
        toast.show({
          variant: 'warning',
          title: t('adminDocumentTypes.toast.defaultProcessTemplateNotPublished'),
        });
        return;
      }
      if (code === 'DEFAULT_PROCESS_TEMPLATE_MODULE_MISMATCH') {
        toast.show({
          variant: 'warning',
          title: t('adminDocumentTypes.toast.defaultProcessTemplateModuleMismatch'),
        });
        return;
      }
      if (handleHrAbsenceSourceErrorCode(code, toast, t)) return;
      toast.show({ variant: 'error', title: t('adminDocumentTypes.toast.updateError') });
    },
  });

  const initialValues = useMemo(() => (type ? toFormValues(type) : null), [type]);

  const handleClose = () => {
    setActiveTab('settings');
    setFormServerErrors({});
    onClose();
  };

  // Zakładka „Ustawienia typu" — placeholder gdy typ się wczytuje/nie wczytał, inaczej
  // embedded formularz (własna stopka Zapisz/Anuluj).
  const settingsContent =
    typeQ.isLoading || !initialValues || !type ? (
      <p className="py-6 text-center text-sm text-text-secondary">
        {typeQ.isError
          ? t('adminKartoteki.typeEditor.loadError')
          : t('adminKartoteki.typeEditor.loading')}
      </p>
    ) : (
      <GlobalDocumentTypeFormModal
        open
        embedded
        mode="edit"
        documentClass={type.class}
        baseFieldOptions={baseFieldOptions}
        initialValues={initialValues}
        codeLocked={type.status !== 'draft'}
        saving={updateMutation.isPending}
        onDirtyChange={setSettingsDirty}
        serverErrors={formServerErrors}
        formViews={formViewOptions}
        formViewsUnavailable={formViewsUnavailable}
        templates={templateOptions}
        templatesUnavailable={templatesUnavailable}
        onSubmit={(values) => {
          setFormServerErrors({});
          updateMutation.mutate({ id: type.id, values, original: type });
        }}
        onClose={handleClose}
      />
    );

  return (
    <Modal
      open={target !== null}
      onClose={handleClose}
      size="2xl"
      fixedHeight
      expandable
      // Zmiany w „Ustawieniach typu": tło/Esc pytają „Porzucić zmiany?". Zapis w toku blokuje tło/Esc.
      guardUnsaved
      dirty={activeTab === 'settings' && settingsDirty}
      closeOnBackdrop={!updateMutation.isPending}
      closeOnEsc={!updateMutation.isPending}
      title={t('adminKartoteki.typeEditor.title', {
        name: type?.name ?? target?.typeName ?? '',
        kartoteka: target?.className ?? '',
      })}
    >
      <Tabs
        idPrefix="document-type-editor"
        value={activeTab}
        onChange={(v) => setActiveTab(v as EditorTab)}
        items={[
          {
            value: 'settings',
            label: t('adminKartoteki.typeEditor.tabSettings'),
            content: settingsContent,
          },
          {
            value: 'fields',
            label: t('adminKartoteki.typeEditor.tabFields'),
            content: target ? (
              <TypeFieldGatingPanel
                slug={target.slug}
                typeId={target.typeId}
                onClose={handleClose}
              />
            ) : null,
          },
        ]}
      />
    </Modal>
  );
}
